import { useState } from "react";
import { useNavigate } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import { Button } from "@nosible/shared/components/ui/button";
import { Badge } from "@nosible/shared/components/ui/badge";
import { Input } from "@nosible/shared/components/ui/input";
import { Label } from "@nosible/shared/components/ui/label";
import { Switch } from "@nosible/shared/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nosible/shared/components/ui/select";
import { trpc } from "@/admin/lib/trpc";
import { ArrowLeft, UserPlus, Check } from "lucide-react";
import { cn } from "@nosible/shared/lib/utils";
import type { AppRouter } from "@nosible/api";
import type { inferRouterOutputs } from "@trpc/server";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type PlanData = RouterOutputs["subscription"]["getActivePlans"][number];

const ROLES = ["user", "admin"] as const;
type Role = (typeof ROLES)[number];

export function AdminUserCreate() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("user");
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [isCreating, setIsCreating] = useState(false);

  const { data: plans, isLoading: plansLoading } = trpc.subscription.getActivePlans.useQuery();

  const createMutation = trpc.admin.createUser.useMutation({
    onSuccess: (data) => {
      navigate(`/users/${data.userId}`);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !name.trim()) return;

    setIsCreating(true);
    try {
      await createMutation.mutateAsync({
        email: email.trim(),
        name: name.trim(),
        role,
        planId: selectedPlanId ?? undefined,
        sendWelcomeEmail,
      });
    } catch {
      setIsCreating(false);
    }
  };

  const formatPrice = (cents: number) => {
    if (cents === 0) return "Free";
    return `$${(cents / 100).toFixed(2)}/mo`;
  };

  const isConflict = createMutation.error?.data?.code === "CONFLICT";

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-lg bg-slate-core flex items-center justify-center">
            <UserPlus className="h-6 w-6 text-chromium-mist" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-silica font-display">Create User</h1>
            <p className="text-chromium-mist">
              Create a user without a password - they sign in via magic link or social login with this email
            </p>
          </div>
        </div>
      </div>

      {createMutation.error && (
        <Card className="mb-6 border-red-500/50">
          <CardContent className="pt-6">
            <p className="text-red-400">
              {isConflict
                ? "A user with this email already exists."
                : createMutation.error.message}
            </p>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>User Details</CardTitle>
            <CardDescription>
              The user will be created with a verified email and no credential account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="create-email">Email Address</Label>
              <Input
                id="create-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="user@example.com"
                required
                disabled={isCreating}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-name">Name</Label>
              <Input
                id="create-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                required
                disabled={isCreating}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="create-role">Role</Label>
              <Select
                value={role}
                onValueChange={(value) => setRole(value as Role)}
                disabled={isCreating}
              >
                <SelectTrigger id="create-role" className="w-full">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      <span className="capitalize">{r}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between pt-2">
              <div>
                <Label htmlFor="create-welcome-email">Send welcome email</Label>
                <p className="text-xs text-chromium-mist mt-1">
                  Sends a welcome email with a durable sign-in link
                </p>
              </div>
              <Switch
                id="create-welcome-email"
                checked={sendWelcomeEmail}
                onCheckedChange={setSendWelcomeEmail}
                disabled={isCreating}
              />
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Plan (Optional)</CardTitle>
            <CardDescription>
              Optionally create an indefinite subscription without Stripe billing
            </CardDescription>
          </CardHeader>
          <CardContent>
            {plansLoading ? (
              <div className="text-center py-8 text-chromium-mist">Loading plans...</div>
            ) : !plans || plans.length === 0 ? (
              <div className="text-center py-8 text-chromium-mist">No active plans available</div>
            ) : (
              <div className="grid gap-4">
                <div
                  onClick={() => setSelectedPlanId(null)}
                  className={cn(
                    "relative p-4 rounded-lg border cursor-pointer transition-all",
                    selectedPlanId === null
                      ? "border-neon-green bg-neon-green/5"
                      : "border-slate-core hover:border-chromium-mist/50"
                  )}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-silica">No Plan</h3>

                        {selectedPlanId === null && (
                          <Badge className="bg-neon-green text-obsidian">
                            <Check className="h-3 w-3 mr-1" />
                            Selected
                          </Badge>
                        )}
                      </div>
                      <p className="text-chromium-mist text-sm mt-1">
                        Create the user without a subscription
                      </p>
                    </div>
                    <div className="ml-4">
                      <div
                        className={cn(
                          "h-5 w-5 rounded-full border-2 flex items-center justify-center",
                          selectedPlanId === null
                            ? "border-neon-green bg-neon-green"
                            : "border-slate-core"
                        )}
                      >
                        {selectedPlanId === null && (
                          <Check className="h-3 w-3 text-obsidian" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {plans.map((plan: PlanData) => (
                  <div
                    key={plan.id}
                    onClick={() => setSelectedPlanId(plan.id)}
                    className={cn(
                      "relative p-4 rounded-lg border cursor-pointer transition-all",
                      selectedPlanId === plan.id
                        ? "border-neon-green bg-neon-green/5"
                        : "border-slate-core hover:border-chromium-mist/50"
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-silica">{plan.name}</h3>

                          {selectedPlanId === plan.id && (
                            <Badge className="bg-neon-green text-obsidian">
                              <Check className="h-3 w-3 mr-1" />
                              Selected
                            </Badge>
                          )}
                        </div>
                        <p className="text-chromium-mist text-sm mt-1">
                          {plan.description || "No description"}
                        </p>
                        <div className="mt-3 flex items-center gap-4 text-sm">
                          <span className="text-silica font-medium">
                            {formatPrice(plan.monthlyPriceCents)}
                          </span>
                          <span className="text-chromium-mist">
                            {plan.defaultApiKeyLimit} API keys
                          </span>

                        </div>
                      </div>
                      <div className="ml-4">
                        <div
                          className={cn(
                            "h-5 w-5 rounded-full border-2 flex items-center justify-center",
                            selectedPlanId === plan.id
                              ? "border-neon-green bg-neon-green"
                              : "border-slate-core"
                          )}
                        >
                          {selectedPlanId === plan.id && (
                            <Check className="h-3 w-3 text-obsidian" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 flex items-center justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!email.trim() || !name.trim() || isCreating}
                className="bg-neon-green text-obsidian hover:bg-neon-green/90"
              >
                {isCreating ? (
                  "Creating..."
                ) : (
                  <>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create User
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
